import { Navigo__SideNavbarSubItemControllerIdentifier } from "controllers/identifiers";
import BaseController from "confinus_ui/base_controller";
import SideNavbarSubItemController from "navigo/side_navbar_sub_item_controller";

export default class SideNavbarItemController extends BaseController {
  public static outlets = [Navigo__SideNavbarSubItemControllerIdentifier];
  declare private readonly navigoSideNavbarSubItemOutlets: SideNavbarSubItemController[];
  private get subMenuItemControllers() {
    return this.navigoSideNavbarSubItemOutlets;
  }

  public static targets = ["title", "arrow", "subMenu", "link"];

  public static values = {
    selected: Boolean,
  };

  declare private readonly selectedValue: boolean;

  declare private readonly arrowTarget: HTMLButtonElement;
  declare private readonly hasArrowTarget: boolean;
  declare private readonly titleTarget: HTMLElement;
  declare private readonly hasTitleTarget: boolean;
  declare private readonly subMenuTarget: HTMLElement;
  declare private readonly hasSubMenuTarget: boolean;
  declare private readonly linkTarget: HTMLElement;
  declare private readonly hasLinkTarget: boolean;

  public static classes = [
    "closed",
    "open",
    "entering",
    "leaving",
    "arrowUp",
    "arrowDown",
    "shown",
    "filtered",
    "active",
    "inactive",
  ];
  declare protected readonly closedClasses: string[];
  declare protected readonly openClasses: string[];
  declare protected readonly enteringClasses: string[];
  declare protected readonly leavingClasses: string[];
  declare protected readonly arrowUpClasses: string[];
  declare protected readonly arrowDownClasses: string[];
  declare protected readonly filteredClasses: string[];
  declare protected readonly shownClasses: string[];
  declare protected readonly activeClasses: string[];
  declare protected readonly inactiveClasses: string[];

  private subMenuOpen = false;
  private readonly resyncOnNavigation = () => this.syncActiveForPath();

  public connect() {
    super.connect();
    this.subMenuOpen = this.selectedValue == true;
    // The sidebar is data-turbo-permanent, so connect() runs once and the
    // server-rendered expand/highlight goes stale across Turbo Drive visits.
    // Re-derive both from the live path after every visit.
    document.addEventListener("turbo:load", this.resyncOnNavigation);
  }

  public disconnect() {
    document.removeEventListener("turbo:load", this.resyncOnNavigation);
    super.disconnect();
  }

  // Re-apply the active group's expand + highlight from the current URL. Only
  // toggles the sub-menu when the desired state actually changes, so a normal
  // full load (server already correct) is a no-op rather than a re-animation.
  private syncActiveForPath() {
    const path = window.location.pathname;

    if (this.hasSubMenuTarget) {
      const anyActive = this.subMenuItemControllers
        .map((sub) => sub.syncActiveForPath(path))
        .some(Boolean);

      if (this.hasLinkTarget) {
        this.toggleTargetElementClasses(
          this.linkTarget,
          anyActive,
          this.inactiveClasses,
          this.activeClasses,
        );
      }
      if (anyActive !== this.subMenuOpen) {
        if (anyActive) {
          this.expand();
        } else {
          this.collapse();
        }
      }
    } else if (this.hasLinkTarget) {
      const href = this.linkTarget.getAttribute("href") || "";
      let linkPath: string;
      try {
        linkPath = new URL(href, window.location.origin).pathname;
      } catch {
        return;
      }
      const active = linkPath === path || path.startsWith(`${linkPath}/`);
      this.toggleTargetElementClasses(
        this.linkTarget,
        active,
        this.inactiveClasses,
        this.activeClasses,
      );
    }
  }

  public buttonClicked() {
    this.subMenuOpen = !this.subMenuOpen;
    this.animateSubMenu();
  }

  public expand() {
    this.subMenuOpen = true;
    this.animateSubMenu();
  }

  public collapse() {
    this.subMenuOpen = false;
    this.animateSubMenu();
  }

  public filterOnSearch(keyword: string): boolean {
    const matchesSubItems =
      this.subMenuItemControllers.filter((item: SideNavbarSubItemController) =>
        item.filterOnSearch(keyword),
      ).length > 0;

    // If anything inside this item matches, expand it
    if (matchesSubItems) {
      this.expand();
    }

    // Check if the item title itself matches the keyword
    const st = keyword.trim().toLowerCase();
    const titleMatches =
      this.hasTitleTarget &&
      st.length >= 2 &&
      this.titleTarget.innerText.toLowerCase().includes(st);

    if (titleMatches) {
      // Title matches - show item and ALL sub-items
      this.subMenuItemControllers.forEach(
        (item: SideNavbarSubItemController) => {
          item.filterOnSearch("");
        },
      );
      this.expand();
      this.setTargetElementClasses(
        this.element,
        this.filteredClasses,
        this.shownClasses,
      );
      return true;
    } else if (!matchesSubItems) {
      // Neither title nor sub-items match
      this.toggleOnSearch(
        keyword,
        this.element,
        [this.titleTarget],
        this.shownClasses,
        this.filteredClasses,
      );
      return false;
    } else {
      // Only sub-items match (not the title) - show only matching sub-items
      this.setTargetElementClasses(
        this.element,
        this.filteredClasses,
        this.shownClasses,
      );
    }
    return matchesSubItems;
  }

  private animateSubMenu() {
    if (this.hasSubMenuTarget) {
      this.toggleTargetElementClasses(
        this.subMenuTarget,
        this.subMenuOpen,
        this.closedClasses,
        this.openClasses,
      );
      setTimeout(() => {
        this.toggleTargetElementClasses(
          this.subMenuTarget,
          this.subMenuOpen,
          this.leavingClasses,
          this.enteringClasses,
        );
      }, 100);
    }

    if (this.hasArrowTarget) {
      this.toggleTargetElementClasses(
        this.arrowTarget,
        this.subMenuOpen,
        this.arrowDownClasses,
        this.arrowUpClasses,
      );
    }
  }
}
